import React from "react"
import { CheckCircle2, Download, Loader2, Play } from "lucide-react"
import { paperDisplayTitle, type PaperSession } from "../types"
import PaperQuestionStem from "./PaperQuestionStem"
import "./ReadyStep.style.scss"

interface ReadyStepProps {
  session: PaperSession
  pdfLoading?: boolean
  starting?: boolean
  onStart: () => void
  onDownloadPdf: () => void
  onCreateAnother: () => void
}

const ReadyStep = ({
  session,
  pdfLoading = false,
  starting = false,
  onStart,
  onDownloadPdf,
  onCreateAnother,
}: ReadyStepProps) => {
  const totalMarks = session.questions.reduce((a, q) => a + q.maxScore, 0)
  const busy = pdfLoading || starting
  const displayTitle = paperDisplayTitle(session)
  const showSubject =
    Boolean(session.subjectTitle?.trim()) &&
    displayTitle !== session.subjectTitle.trim()

  return (
    <div className="ready-step">
      <div className="ready-step__success">
        <CheckCircle2 size={36} />
      </div>
      <h2>Paper Generated!</h2>
      <p className="ready-step__subject">{displayTitle}</p>
      <p className="ready-step__meta">
        {[
          showSubject ? session.subjectTitle : null,
          `${session.questions.length} Questions`,
          `${totalMarks} total marks`,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>

      <div className="ready-step__preview">
        {session.questions.slice(0, 5).map((q, i) => (
          <div key={`${q.questionId}-${i}`} className="ready-step__preview-row">
            <span className="ready-step__qnum">Q{i + 1}</span>
            <span className="ready-step__qtext">
              <PaperQuestionStem
                html={q.questionText}
                artifacts={q.questionArtifacts}
                compact
              />
            </span>
            <span className="ready-step__marks">[{q.maxScore}m]</span>
          </div>
        ))}
        {session.questions.length > 5 && (
          <p className="ready-step__more">
            + {session.questions.length - 5} more questions…
          </p>
        )}
      </div>

      <div className="ready-step__ctas">
        <button
          type="button"
          className="is-primary"
          onClick={onStart}
          disabled={busy}
          aria-busy={starting}
        >
          {starting ? (
            <>
              <Loader2 size={16} className="is-spinning" /> Starting…
            </>
          ) : (
            <>
              <Play size={16} /> Start Paper Now
            </>
          )}
        </button>
        <button
          type="button"
          className="is-secondary"
          onClick={onDownloadPdf}
          disabled={busy}
          aria-busy={pdfLoading}
        >
          {pdfLoading ? (
            <>
              <Loader2 size={16} className="is-spinning" /> Preparing PDF…
            </>
          ) : (
            <>
              <Download size={16} /> Download PDF
            </>
          )}
        </button>
      </div>

      <div className="ready-step__again">
        <button type="button" onClick={onCreateAnother} disabled={busy}>
          Create another paper
        </button>
      </div>
    </div>
  )
}

export default ReadyStep
